import { useState, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PencilIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  CheckIcon,
  XMarkIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { Listbox, Transition } from '@headlessui/react';
import { useData } from '../../context/DataContext';
import Breadcrumbs from '../common/Breadcrumbs';
import {
  calculateMultiPortfolioGoalValue,
  calculateProgressPercentage,
  calculateGoalStatus,
  calculateGoalProjection,
  getGoalContributions,
  calculateGoalAllocationBreakdown,
  calculateGoalMonthlyContribution,
} from '../../utils/calculations';
import {
  formatCurrency,
  formatPercentageWithSign,
  getStatusColor,
} from '../../utils/formatters';
import { getGoalIcon } from '../../utils/goalIcons';
import { getCurrencySymbol } from '../../utils/formatters';
import { Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, ComposedChart, ReferenceLine, Legend } from 'recharts';
import GoalModal from './GoalModal';
import AllocationModal from '../Portfolio/AllocationModal';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

const GoalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { portfolios, goals, transactions, exchangeRates, updateGoal } = useData();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [expandedAllocationId, setExpandedAllocationId] = useState(null);
  const [editingAllocation, setEditingAllocation] = useState(null);
  const [chartAggregation, setChartAggregation] = useState('monthly');
  const [isAllocationsExpanded, setIsAllocationsExpanded] = useState(true);
  const [isAnalysisExpanded, setIsAnalysisExpanded] = useState(true);
  const [isAllocationModalVisible, setIsAllocationModalVisible] = useState(false);
  const [selectedPortfolioForAllocation, setSelectedPortfolioForAllocation] = useState(null);

  const goal = goals.find((g) => g.id === id);

  if (!goal) {
    return (
      <div className="p-6">
        <p className="text-gray-600">Fund not found</p>
      </div>
    );
  }

  const GoalIcon = getGoalIcon(goal.icon || goal.emoji);
  const currentValue = calculateMultiPortfolioGoalValue(goal, portfolios, exchangeRates);
  const progress = calculateProgressPercentage(currentValue, goal.targetAmount);
  const goalStatus = calculateGoalStatus(goal, currentValue, portfolios, exchangeRates);
  const contributions = getGoalContributions(goal.id, transactions, portfolios, goal.currency, exchangeRates);
  const allocationBreakdown = calculateGoalAllocationBreakdown(goal, portfolios, exchangeRates);
  const projectionResult = calculateGoalProjection(goal, portfolios, transactions, exchangeRates, null);
  const lowProjection = projectionResult.lowProjection || [];
  const averageProjection = projectionResult.averageProjection || [];
  const highProjection = projectionResult.highProjection || [];
  const goalReachDateLow = projectionResult.goalReachDateLow;
  const goalReachDateAverage = projectionResult.goalReachDateAverage;
  const goalReachDateHigh = projectionResult.goalReachDateHigh;
  const monthlyContribution = calculateGoalMonthlyContribution(goal, portfolios, exchangeRates);

  // Merge projection data for chart
  const rawProjectionChartData = averageProjection.map((avgPoint, index) => ({
    ...avgPoint,
    lowValue: lowProjection[index]?.value || avgPoint.value,
    averageValue: avgPoint.value,
    highValue: highProjection[index]?.value || avgPoint.value,
  }));

  // Aggregate chart data based on selected breakdown
  const aggregateChartData = (data, aggregation) => {
    if (aggregation === 'monthly') {
      return data; // No aggregation needed for monthly
    }

    // Yearly aggregation - keep only December data points (or last month of year)
    const aggregated = [];
    const yearlyData = {};

    data.forEach(point => {
      const date = dayjs(point.date);
      const year = date.year();

      // Always keep the latest data point for each year
      if (!yearlyData[year]) {
        yearlyData[year] = point;
      } else {
        yearlyData[year] = point;
      }
    });

    // Convert to array and update display dates for yearly view
    Object.keys(yearlyData).sort().forEach(year => {
      const point = { ...yearlyData[year] };
      point.displayDate = dayjs(point.date).format('YYYY');
      aggregated.push(point);
    });

    return aggregated;
  };

  const projectionChartData = aggregateChartData(rawProjectionChartData, chartAggregation);

  // Handlers for allocation editing
  const handleToggleExpand = (portfolioId) => {
    if (expandedAllocationId === portfolioId) {
      setExpandedAllocationId(null);
      setEditingAllocation(null);
    } else {
      setExpandedAllocationId(portfolioId);
      // Find the existing allocation for this portfolio
      const existingAlloc = goal.allocations?.find(a => a.portfolioId === portfolioId);
      if (existingAlloc) {
        // Fix: Look at the allocation in allocationBreakdown to get the correct type
        const breakdownAlloc = allocationBreakdown.find(a => a.portfolioId === portfolioId);
        setEditingAllocation({
          portfolioId,
          type: breakdownAlloc?.allocationType || existingAlloc.type || 'percentage',
          value: existingAlloc.value || 0,
        });
      }
    }
  };

  const handleOpenAllocationModal = (portfolioId = null) => {
    setSelectedPortfolioForAllocation(portfolioId);
    setIsAllocationModalVisible(true);
  };

  const handleCloseAllocationModal = () => {
    setIsAllocationModalVisible(false);
    setSelectedPortfolioForAllocation(null);
  };

  const handleSaveAllocation = () => {
    if (!editingAllocation) return;

    const updatedAllocations = [...(goal.allocations || [])];
    const existingIndex = updatedAllocations.findIndex(a => a.portfolioId === editingAllocation.portfolioId);

    if (existingIndex >= 0) {
      updatedAllocations[existingIndex] = {
        ...updatedAllocations[existingIndex],
        type: editingAllocation.type,
        value: parseFloat(editingAllocation.value) || 0,
      };
    } else {
      updatedAllocations.push({
        portfolioId: editingAllocation.portfolioId,
        type: editingAllocation.type,
        value: parseFloat(editingAllocation.value) || 0,
      });
    }

    updateGoal(goal.id, { allocations: updatedAllocations });
    toast.success('Allocation updated');
    setExpandedAllocationId(null);
    setEditingAllocation(null);
  };

  const handleCancelEdit = () => {
    setExpandedAllocationId(null);
    setEditingAllocation(null);
  };

  // Calculate projected value at target date
  const targetDate = goal.targetDate ? dayjs(goal.targetDate) : null;
  const monthsToTarget = targetDate ? Math.max(0, targetDate.diff(dayjs(), 'month', true)) : null;
  const projectedAtTargetLow = lowProjection.length > 0 && monthsToTarget !== null && monthsToTarget > 0
    ? lowProjection[Math.min(Math.floor(monthsToTarget), lowProjection.length - 1)]?.value || currentValue
    : (lowProjection.length > 0 ? lowProjection[lowProjection.length - 1]?.value || currentValue : currentValue);
  const projectedAtTargetAverage = averageProjection.length > 0 && monthsToTarget !== null && monthsToTarget > 0
    ? averageProjection[Math.min(Math.floor(monthsToTarget), averageProjection.length - 1)]?.value || currentValue
    : (averageProjection.length > 0 ? averageProjection[averageProjection.length - 1]?.value || currentValue : currentValue);
  const projectedAtTargetHigh = highProjection.length > 0 && monthsToTarget !== null && monthsToTarget > 0
    ? highProjection[Math.min(Math.floor(monthsToTarget), highProjection.length - 1)]?.value || currentValue
    : (highProjection.length > 0 ? highProjection[highProjection.length - 1]?.value || currentValue : currentValue);

  // Format months to years and months
  const formatMonthsRemaining = (months) => {
    if (months === null || months === undefined) return '';
    const years = Math.floor(months / 12);
    const remainingMonths = Math.round(months % 12);

    if (years === 0) {
      return `${remainingMonths} ${remainingMonths === 1 ? 'month' : 'months'} remaining`;
    } else if (remainingMonths === 0) {
      return `${years} ${years === 1 ? 'year' : 'years'} remaining`;
    } else {
      return `${years} ${years === 1 ? 'year' : 'years'} ${remainingMonths} ${remainingMonths === 1 ? 'month' : 'months'} remaining`;
    }
  };

  return (
    <div>
      {/* Breadcrumbs */}
      <Breadcrumbs
        items={[
          { label: 'Funds', link: '/funds' },
          { label: goal.name },
        ]}
      />

      {/* Header with Edit button */}
      <div className="flex flex-row justify-end items-center mb-4">
        <button
          onClick={() => setIsModalVisible(true)}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-colors"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
        >
          <PencilIcon className="w-5 h-5" />
          <span>Edit Fund</span>
        </button>
      </div>

      {/* Goal Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
              <GoalIcon className="w-7 h-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 m-0">{goal.name}</h1>
              <p className="text-xs text-gray-600 mt-0.5">
                {targetDate && `Target: ${targetDate.format('MMM YYYY')}`}
                {goalReachDateLow && (
                  <span className="ml-2 text-red-600 font-medium">
                    • Low: {goalReachDateLow.format('MMM YYYY')}
                  </span>
                )}
                {goalReachDateAverage && (
                  <span className="ml-2 text-violet-600 font-medium">
                    • Avg: {goalReachDateAverage.format('MMM YYYY')}
                  </span>
                )}
                {goalReachDateHigh && (
                  <span className="ml-2 text-emerald-600 font-medium">
                    • High: {goalReachDateHigh.format('MMM YYYY')}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div>
            <span
              className="inline-block text-xs text-white uppercase px-2 py-0.5 rounded-full font-medium"
              style={{ backgroundColor: getStatusColor(goalStatus.status) }}
            >
              {goalStatus.status}
            </span>
          </div>
        </div>

        {/* Key Metrics Cards */}
        {goal.targetAmount > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2 mb-3">
            <div className="bg-gray-50 rounded p-2 border border-gray-200">
              <div className="text-xs text-gray-600 mb-0.5">Current Value</div>
              <div className="text-lg font-semibold text-gray-900">
                {formatCurrency(currentValue, goal.currency, 0)}
              </div>
              {contributions > 0 && (
                <div className="text-xs text-gray-500 mt-0.5">
                  Contributions: {formatCurrency(contributions, goal.currency, 0)}
                </div>
              )}
            </div>
            <div className="bg-red-50 rounded p-2 border border-red-200">
              <div className="text-xs text-red-600 mb-0.5">Projected (Low)</div>
              <div className="text-lg font-semibold text-red-900">
                {formatCurrency(projectedAtTargetLow, goal.currency, 0)}
              </div>
              <div className="text-xs text-red-600 mt-0.5">
                {projectedAtTargetLow >= goal.targetAmount ? '✓ On track' : 'Needs more'}
              </div>
            </div>
            <div className="bg-violet-50 rounded p-2 border border-violet-200">
              <div className="text-xs text-violet-600 mb-0.5">Projected (Avg)</div>
              <div className="text-lg font-semibold text-violet-900">
                {formatCurrency(projectedAtTargetAverage, goal.currency, 0)}
              </div>
              <div className="text-xs text-violet-600 mt-0.5">
                {projectedAtTargetAverage >= goal.targetAmount ? '✓ On track' : 'Needs more'}
              </div>
            </div>
            <div className="bg-emerald-50 rounded p-2 border border-emerald-200">
              <div className="text-xs text-emerald-600 mb-0.5">Projected (High)</div>
              <div className="text-lg font-semibold text-emerald-900">
                {formatCurrency(projectedAtTargetHigh, goal.currency, 0)}
              </div>
              <div className="text-xs text-emerald-600 mt-0.5">
                {projectedAtTargetHigh >= goal.targetAmount ? '✓ On track' : 'Needs more'}
              </div>
            </div>
            <div className="bg-green-50 rounded p-2 border border-green-200">
              <div className="text-xs text-green-600 mb-0.5">Target Amount</div>
              <div className="text-lg font-semibold text-green-900">
                {formatCurrency(goal.targetAmount, goal.currency, 0)}
              </div>
              {targetDate && monthsToTarget !== null && (
                <div className="text-xs text-green-600 mt-0.5">
                  {formatMonthsRemaining(monthsToTarget)}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
            <div className="bg-gray-50 rounded p-2 border border-gray-200">
              <div className="text-xs text-gray-600 mb-0.5">Current Value</div>
              <div className="text-lg font-semibold text-gray-900">
                {formatCurrency(currentValue, goal.currency, 0)}
              </div>
              {contributions > 0 && (
                <div className="text-xs text-gray-500 mt-0.5">
                  Contributions: {formatCurrency(contributions, goal.currency, 0)}
                </div>
              )}
            </div>
            <div className="bg-gray-50 rounded p-2 border border-gray-200">
              <div className="text-xs text-gray-600 mb-0.5">Monthly Contribution</div>
              <div className="text-lg font-semibold text-gray-900">
                {formatCurrency(monthlyContribution, goal.currency, 0)}
              </div>
              {goal.allocations && goal.allocations.length > 0 && (
                <div className="text-xs text-gray-500 mt-0.5">
                  from {goal.allocations.length} account{goal.allocations.length > 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Progress Bar — only shown when there's a target */}
        {goal.targetAmount > 0 && (
          <div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.min(100, progress)}%`,
                  backgroundColor: goal.color || '#2a4d6e',
                }}
              />
            </div>
            <div className="text-center mt-0.5">
              <span className="text-xs text-gray-600">{progress.toFixed(1)}% complete</span>
            </div>
          </div>
        )}
      </div>

      {/* Projection Chart */}
      {projectionChartData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-900 m-0">Projected Value Over Time</h3>
            <div className="flex items-center gap-4">
              {/* Goal Reach Dates */}
              <div className="flex gap-4 text-xs">
                {goalReachDateLow && (
                  <span className="text-red-600 font-medium">
                    Low: {goalReachDateLow.format('MMM YYYY')}
                  </span>
                )}
                {goalReachDateAverage && (
                  <span className="text-violet-600 font-medium">
                    Average: {goalReachDateAverage.format('MMM YYYY')}
                  </span>
                )}
                {goalReachDateHigh && (
                  <span className="text-emerald-600 font-medium">
                    High: {goalReachDateHigh.format('MMM YYYY')}
                  </span>
                )}
              </div>

              {/* Breakdown */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Breakdown:</span>
                <Listbox value={chartAggregation} onChange={setChartAggregation}>
                  <div className="relative">
                    <Listbox.Button className="relative cursor-pointer rounded-lg bg-white py-1.5 pl-3 pr-8 text-left border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs min-w-[80px]">
                      <span className="block truncate capitalize">{chartAggregation}</span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-32 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-xs right-0">
                        {['monthly', 'yearly'].map((agg) => (
                          <Listbox.Option
                            key={agg}
                            value={agg}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 pl-8 pr-4 ${
                                active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                              }`
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span className={`block truncate capitalize ${selected ? 'font-semibold' : 'font-normal'}`}>
                                  {agg}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-blue-600">
                                    <CheckIcon className="h-4 w-4" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={projectionChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorLow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.5}/>
                  <stop offset="50%" stopColor="#f87171" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#fca5a5" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorAverage" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.5}/>
                  <stop offset="50%" stopColor="#a78bfa" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#c4b5fd" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorHigh" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.5}/>
                  <stop offset="50%" stopColor="#34d399" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6ee7b7" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <XAxis
                dataKey="displayDate"
                stroke="#9ca3af"
                style={{ fontSize: '12px' }}
                tick={{ fill: '#6b7280' }}
                interval="preserveStartEnd"
              />
              <YAxis
                stroke="#9ca3af"
                style={{ fontSize: '12px' }}
                tick={{ fill: '#6b7280' }}
                tickFormatter={(value) => formatCurrency(value, goal.currency, 0)}
              />
              <Legend
                wrapperStyle={{ fontSize: '13px', paddingTop: '10px' }}
                iconType="line"
              />
              <Tooltip
                allowEscapeViewBox={{ x: false, y: true }}
                wrapperStyle={{ zIndex: 1000, pointerEvents: 'none' }}
                position={{ y: 0 }}
                content={({ active, payload, label, coordinate, viewBox }) => {
                  if (!active || !payload) return null;

                  // Find current and previous data points
                  const currentIndex = projectionChartData.findIndex(d => d.displayDate === label);
                  const previousData = currentIndex > 0 ? projectionChartData[currentIndex - 1] : null;

                  // Filter to show only unique dataKeys (avoid duplicates from Area + Line)
                  const uniqueData = {};
                  payload.forEach(entry => {
                    if (entry.value != null && !uniqueData[entry.dataKey]) {
                      uniqueData[entry.dataKey] = entry;
                    }
                  });

                  const labels = {
                    lowValue: 'Low',
                    averageValue: 'Average',
                    highValue: 'High',
                    contributions: 'Contributions',
                  };

                  // Calculate if we're near the right edge (within 200px of the right side)
                  const isNearRightEdge = coordinate && viewBox && (viewBox.x + viewBox.width - coordinate.x < 200);

                  return (
                    <div style={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: '13px',
                      padding: '8px 12px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                      transform: isNearRightEdge ? 'translateX(-100%)' : 'translateX(10px)',
                      marginLeft: isNearRightEdge ? '-10px' : '0px',
                    }}>
                      <p style={{ marginBottom: '4px', fontWeight: 600 }}>{label}</p>
                      {Object.values(uniqueData).map((entry, index) => {
                        const currentValue = entry.value;
                        const previousValue = previousData?.[entry.dataKey];
                        const change = previousValue != null ? currentValue - previousValue : null;
                        const percentChange = previousValue != null && previousValue !== 0
                          ? ((currentValue - previousValue) / previousValue) * 100
                          : null;

                        return (
                          <div key={index} style={{ margin: '4px 0' }}>
                            <p style={{ margin: 0, color: entry.color }}>
                              <span style={{ fontWeight: 500 }}>{labels[entry.dataKey] || entry.dataKey}:</span>{' '}
                              {formatCurrency(currentValue, goal.currency, 0)}
                            </p>
                            {change != null && (
                              <p style={{
                                margin: 0,
                                marginLeft: '8px',
                                fontSize: '11px',
                                color: change >= 0 ? '#10b981' : '#ef4444',
                                fontWeight: 500
                              }}>
                                {change >= 0 ? '↑' : '↓'} {formatCurrency(Math.abs(change), goal.currency, 0)} ({percentChange >= 0 ? '+' : ''}{percentChange.toFixed(1)}%)
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              />
              {/* Low Projection Area - hidden from legend */}
              <Area
                type="monotone"
                dataKey="lowValue"
                stroke="none"
                fill="url(#colorLow)"
                fillOpacity={1}
                isAnimationActive={false}
                legendType="none"
              />
              {/* Average Projection Area - hidden from legend */}
              <Area
                type="monotone"
                dataKey="averageValue"
                stroke="none"
                fill="url(#colorAverage)"
                fillOpacity={1}
                isAnimationActive={false}
                legendType="none"
              />
              {/* High Projection Area - hidden from legend */}
              <Area
                type="monotone"
                dataKey="highValue"
                stroke="none"
                fill="url(#colorHigh)"
                fillOpacity={1}
                isAnimationActive={false}
                legendType="none"
              />
              {/* Low Projection Line */}
              <Line
                type="monotone"
                dataKey="lowValue"
                stroke="#dc2626"
                strokeWidth={3}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  // Show dot where line crosses target
                  if (payload.lowValue >= goal.targetAmount &&
                      projectionChartData[props.index - 1]?.lowValue < goal.targetAmount) {
                    return (
                      <circle cx={cx} cy={cy} r={6} fill="#dc2626" stroke="#fff" strokeWidth={2} />
                    );
                  }
                  return null;
                }}
                name="Low"
                isAnimationActive={false}
              />
              {/* Average Projection Line */}
              <Line
                type="monotone"
                dataKey="averageValue"
                stroke="#7c3aed"
                strokeWidth={3}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  // Show dot where line crosses target
                  if (payload.averageValue >= goal.targetAmount &&
                      projectionChartData[props.index - 1]?.averageValue < goal.targetAmount) {
                    return (
                      <circle cx={cx} cy={cy} r={6} fill="#7c3aed" stroke="#fff" strokeWidth={2} />
                    );
                  }
                  return null;
                }}
                name="Average"
                isAnimationActive={false}
              />
              {/* High Projection Line */}
              <Line
                type="monotone"
                dataKey="highValue"
                stroke="#059669"
                strokeWidth={3}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  // Show dot where line crosses target
                  if (payload.highValue >= goal.targetAmount &&
                      projectionChartData[props.index - 1]?.highValue < goal.targetAmount) {
                    return (
                      <circle cx={cx} cy={cy} r={6} fill="#059669" stroke="#fff" strokeWidth={2} />
                    );
                  }
                  return null;
                }}
                name="High"
                isAnimationActive={false}
              />
              {goal.targetAmount > 0 && (
                <ReferenceLine
                  y={goal.targetAmount}
                  stroke="#10b981"
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  label={{ value: 'Target', position: 'right', fill: '#10b981', fontSize: 12, fontWeight: 600 }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Analysis Section */}
      {goalStatus.monthsRemaining > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-4">
          <div
            className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setIsAnalysisExpanded(!isAnalysisExpanded)}
          >
            <h3 className="text-base font-semibold text-gray-900 m-0">Analysis</h3>
            {isAnalysisExpanded ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-500" />
            )}
          </div>
          {isAnalysisExpanded && (
            <div className="px-4 pb-4 border-t border-gray-200 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm mb-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Time Remaining:</span>
              <span className="font-medium text-gray-900">{goalStatus.monthsRemaining.toFixed(1)} months</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Monthly Contribution:</span>
              <span className="font-medium text-gray-900">
                {formatCurrency(monthlyContribution, goal.currency, 0)}
                {goal.allocations && goal.allocations.length > 0 && (
                  <span className="text-xs text-gray-500 ml-1">
                    (from {goal.allocations.length} account{goal.allocations.length > 1 ? 's' : ''})
                  </span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Required Monthly:</span>
              <span className="font-medium" style={{ color: goalStatus.isOnTrack ? '#10b981' : '#ef4444' }}>
                {formatCurrency(goalStatus.requiredMonthly, goal.currency, 0)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Remaining to Goal:</span>
              <span className="font-medium text-gray-900">{formatCurrency(Math.max(0, goal.targetAmount - currentValue), goal.currency, 0)}</span>
            </div>
          </div>
          {!goalStatus.isOnTrack && goalStatus.monthsRemaining > 0 && (
            <div className="p-3 bg-orange-50 rounded border border-orange-200">
              <p className="font-medium text-orange-700 text-xs m-0">
                ⚠️ You need to increase your monthly contribution by{' '}
                {formatCurrency(
                  goalStatus.requiredMonthly - monthlyContribution,
                  goal.currency,
                  0
                )}{' '}
                to stay on track. Update your account contributions to allocate more towards this goal.
              </p>
            </div>
          )}
          {goalStatus.isOnTrack && (
            <div className="p-3 bg-green-50 rounded border border-green-200">
              <p className="font-medium text-green-700 text-xs m-0">
                ✅ You're on track to reach your goal!
              </p>
            </div>
          )}
            </div>
          )}
        </div>
      )}

      {/* Allocation Breakdown */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div
          className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsAllocationsExpanded(!isAllocationsExpanded)}
        >
          <h3 className="text-base font-semibold text-gray-900 m-0">Account Allocations</h3>
          <div className="flex items-center gap-2">
            {allocationBreakdown.length > 0 && (
              <span className="text-xs text-gray-500">
                {allocationBreakdown.length} account{allocationBreakdown.length > 1 ? 's' : ''}
              </span>
            )}
            {isAllocationsExpanded ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-500" />
            )}
          </div>
        </div>
        {isAllocationsExpanded && (
        <div className="px-4 pb-4 border-t border-gray-200 pt-4">
          <div className="flex justify-between items-center mb-3">
            <button
              onClick={() => handleOpenAllocationModal()}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: '#012E58' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
            >
              <PlusIcon className="w-4 h-4" />
              Add Allocation
            </button>
            <button
              onClick={() => navigate('/allocations')}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              View All Allocations →
            </button>
          </div>

          {allocationBreakdown.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">
              No allocations yet. Click "Add Allocation" to get started.
            </p>
          )}

          {allocationBreakdown.length > 0 && (
          <div className="space-y-2">
            {allocationBreakdown.map((allocation) => {
              const isExpanded = expandedAllocationId === allocation.portfolioId;
              const portfolio = portfolios.find(p => p.id === allocation.portfolioId);

              return (
                <div key={allocation.portfolioId} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Allocation Summary - Clickable to expand */}
                  <div
                    className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                    onClick={() => handleToggleExpand(allocation.portfolioId)}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      {isExpanded ? (
                        <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                      )}
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 text-sm">{allocation.portfolioName}</div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          {allocation.allocationType === 'percentage'
                            ? `${allocation.allocationValue}% allocated`
                            : `${formatCurrency(allocation.allocationValue, allocation.currency, 0)} allocated`}
                          {' • '}
                          Est. return: {allocation.returnRate}% p.a.
                        </div>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-base font-semibold text-gray-900">
                        {formatCurrency(allocation.allocatedValue, goal.currency, 0)}
                      </div>
                      <div className={`text-xs ${allocation.allocatedGain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {(() => {
                          const costBasis = allocation.allocatedValue - allocation.allocatedGain;
                          const returnPct = costBasis > 0 ? (allocation.allocatedGain / costBasis) * 100 : 0;
                          return formatPercentageWithSign(returnPct);
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Edit Section */}
                  {isExpanded && editingAllocation && (
                    <div className="p-4 bg-white border-t border-gray-200">
                      <div className="flex flex-col gap-3">
                        <div className="flex gap-3 items-end">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Allocation Type
                            </label>
                            <select
                              value={editingAllocation.type}
                              onChange={(e) => setEditingAllocation({ ...editingAllocation, type: e.target.value })}
                              className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="percentage">Percentage (%)</option>
                              <option value="amount">Amount ({getCurrencySymbol(portfolio?.currency || 'GBP')})</option>
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Value
                            </label>
                            <input
                              type="number"
                              value={editingAllocation.value}
                              onChange={(e) => setEditingAllocation({ ...editingAllocation, value: e.target.value })}
                              min={0}
                              max={editingAllocation.type === 'percentage' ? 100 : undefined}
                              step={editingAllocation.type === 'percentage' ? 0.1 : 1}
                              className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>

                        <div className="flex gap-2 justify-between">
                          <button
                            onClick={() => navigate(`/portfolio/${allocation.portfolioId}`)}
                            className="text-xs text-gray-600 hover:text-gray-700 font-medium transition-colors"
                          >
                            View Account →
                          </button>
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelEdit();
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              <XMarkIcon className="w-3.5 h-3.5" />
                              Cancel
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveAllocation();
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors"
                              style={{ backgroundColor: '#012E58' }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
                            >
                              <CheckIcon className="w-3.5 h-3.5" />
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          )}
        </div>
        )}
      </div>

      <GoalModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        goal={goal}
      />

      <AllocationModal
        visible={isAllocationModalVisible}
        onCancel={handleCloseAllocationModal}
        portfolioId={selectedPortfolioForAllocation}
        goalId={goal.id}
        allocation={null}
      />
    </div>
  );
};

export default GoalDetail;
